import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { routesApi } from '../api/routesApi';
import type { RouteFilters, RouteFormValues } from '../types/route';

export function useRoutesQuery(filters: RouteFilters, page: number) {
  return useQuery({
    queryKey: ['gateway', 'routes', filters, page],
    queryFn: () => routesApi.list({ ...filters, page, size: 20, sort: 'createdAt,desc' }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useRoutesSummary() {
  const total = useQuery({
    queryKey: ['gateway', 'routes', 'summary', 'total'],
    queryFn: () => routesApi.list({ page: 0, size: 1 }),
    staleTime: 60_000,
    select: (data) => data.totalElements,
  });
  const active = useQuery({
    queryKey: ['gateway', 'routes', 'summary', 'active'],
    queryFn: () => routesApi.list({ enabled: true, page: 0, size: 1 }),
    staleTime: 60_000,
    select: (data) => data.totalElements,
  });
  return {
    totalRoutes: total.data ?? 0,
    activeRoutes: active.data ?? 0,
    isLoading: total.isLoading || active.isLoading,
  };
}

export function useRouteQuery(id: string | null) {
  return useQuery({
    queryKey: ['gateway', 'route', id],
    queryFn: () => routesApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useServiceOptions() {
  return useQuery({
    queryKey: ['gateway', 'serviceOptions'],
    queryFn: () => routesApi.listServices(),
    staleTime: 5 * 60_000,
  });
}

export function useCreateRoute(
  onSuccess?: (name: string) => void,
  onError?: (err: unknown) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RouteFormValues) => routesApi.create(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['gateway', 'routes'] });
      onSuccess?.(created.name);
    },
    onError,
  });
}

export function useReplaceRoute(
  id: string,
  onSuccess?: () => void,
  onError?: (err: unknown) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RouteFormValues) => routesApi.replace(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'routes'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'route', id] });
      onSuccess?.();
    },
    onError,
  });
}

export function usePatchRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<RouteFormValues> }) =>
      routesApi.patch(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['gateway', 'routes'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'route', id] });
    },
  });
}

/**
 * #447 (UAT 2026-05-14): onError 콜백 지원 — confirm 다이얼로그가 토스트로 에러 메시지를 노출하기 위함.
 */
export function useDeleteRoute(
  onSuccess?: () => void,
  onError?: (err: unknown) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => routesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'routes'] });
      onSuccess?.();
    },
    onError,
  });
}
